// Reusable, presentational SOURCE CARD - the unit of the right-side "Explore Next"
// panel (and reusable anywhere a connected source is offered). It is deliberately
// NOT a `.srow`: it carries no data-ref the trail walks and never joins the live
// DOM path, so DOM-path-as-truth is untouched (see navigator.ts). It is a pure
// projection - the caller resolves the honest type/relationship/colour from real
// Sefaria fields and passes a view-model + action callbacks. Every string is set
// via textContent (never innerHTML), and color is ALWAYS paired with a text label
// so it is never the sole signal.

import { el } from "../lib/dom.ts";
import type { TrustTone } from "../lib/sourceTrust.ts";

// The typed connection the card renders. `id` is the real Sefaria ref (also the
// key). All display fields come from real fields the caller already verified.
export interface SourceConnection {
  id: string; // the canonical Sefaria ref, e.g. "Menachot 43b"
  title: string; // English display label
  hebrewTitle?: string | null;
  contextLine?: string | null;
  type?: string; // honest source-type label, e.g. "Commentary" / "Gemara"
  // Learner-visible source-kind label (Talmud / Midrash / Tanakh / ...), rendered
  // always when set - distinct from `type` (debug-only badge). Set for the
  // "Where else this comes up" bucket, whose members are a mix of kinds and need
  // the kind named so the catch-all stops being a black box.
  kind?: string | null;
  category?: string; // raw Sefaria category for persistence/filtering
  colorVar?: string; // source-type ink var, e.g. "--t-commentary" (paired with label)
  relationship: string; // honest, anchor-agnostic, e.g. "comments on this"
  strength?: "strong" | "medium" | "weak" | null;
  length?: "short" | "medium" | "long" | null;
  reason?: string | null; // short "why this source appears" line (honest, no fetch)
  parentId?: string;
  onDesk?: boolean;
  bookmarked?: boolean;
  // Trust badge derived from the SourceEdge + Evidence behind this connection
  // (sourceTrust.getTrustBadge). Tone is never the sole signal - the label is
  // always rendered as text. AI-suggested/suggested must read quieter than
  // verified (styling keys off data-tone).
  trust?: { label: string; tone: TrustTone } | null;
}

export interface SourceCardActions {
  onPreview?: (c: SourceConnection) => void;
  onContinue?: (c: SourceConnection) => void;
  onAddToDesk?: (c: SourceConnection, btn: HTMLButtonElement) => void;
  onBookmark?: (c: SourceConnection, btn: HTMLButtonElement) => void;
  // "Why this source?" - opens the evidence/provenance explanation.
  onWhy?: (c: SourceConnection) => void;
}

const STRENGTH_LABEL: Record<NonNullable<SourceConnection["strength"]>, string> = {
  strong: "strong link",
  medium: "related",
  weak: "thematic",
};

// Build the card. Buttons render only when the matching callback is supplied, so a
// not-yet-wired action degrades to simply being absent (graceful, never a dead
// control). The card's actions are the explicit Preview / Open / Desk / Bookmark
// buttons - the head is identity only (it carries no click handler, so it never
// competes with text selection or the action buttons).
//
// opts.debug (isSourceDebug): the type badge, relationship/trust/strength/length
// chips and the reason line are INSPECTION vocabulary - they render only for
// developers/QA. Learners get a calm card: titles + actions; the surrounding
// group heading supplies the context ("Commentaries" etc). The trust machinery
// behind the card keeps running either way - this gates rendering only.
export function renderSourceCard(
  c: SourceConnection,
  actions: SourceCardActions = {},
  opts: { debug?: boolean } = {},
): HTMLElement {
  const card = el("article", "xcard");
  if (c.colorVar) card.style.setProperty("--tc", `var(${c.colorVar})`);
  card.dataset.ref = c.id; // for the panel to find/mirror; NOT walked by replayPath

  const head = el("div", "xcard-head");
  const dot = el("span", "ec-dot xcard-dot");
  if (c.colorVar) dot.style.setProperty("--dot", `var(${c.colorVar})`);
  dot.setAttribute("aria-hidden", "true");

  const titles = el("div", "xcard-titles");
  if (c.hebrewTitle) {
    const he = el("span", "xcard-he", c.hebrewTitle);
    he.lang = "he";
    he.dir = "rtl";
    titles.append(he);
  }
  titles.append(el("span", "xcard-en", c.title));
  if (c.contextLine) titles.append(el("div", "xcard-context", c.contextLine));
  head.append(dot, titles);
  // The learner-visible kind label (always on when set) - names what this
  // connection IS (Talmud / Midrash / ...) so the "Where else this comes up"
  // pile reads as a map, not a mystery. Color is never the sole signal.
  if (c.kind) head.append(el("span", "xcard-kind", c.kind));
  if (opts.debug && c.type) head.append(el("span", "xcard-badge", c.type));
  card.append(head);

  if (opts.debug) {
    // Honest relationship chip (color never the sole signal); debug extends
    // the same row with the trust/strength/length inspection chips.
    const chips = el("div", "xcard-chips");
    chips.append(relChip(c.relationship));
    if (opts.debug) {
      if (c.trust) {
        const t = el("span", "trust-chip", c.trust.label);
        t.dataset.tone = c.trust.tone;
        chips.append(t);
      }
      if (c.strength) {
        const s = el("span", "str-chip", STRENGTH_LABEL[c.strength]);
        s.dataset.strength = c.strength;
        chips.append(s);
      }
      if (c.length) chips.append(el("span", "len-chip", `${c.length} read`));
    }
    card.append(chips);

    if (opts.debug && c.reason) card.append(el("p", "xcard-why", c.reason));
  }

  const acts = el("div", "xcard-acts");
  if (actions.onPreview) {
    acts.append(actBtn("xcard-btn", "Preview", `Peek at ${c.title} without opening it`, () => actions.onPreview?.(c)));
  }
  if (actions.onContinue) {
    acts.append(actBtn("xcard-btn xcard-btn-go", "Open", openTip(c.title), () => actions.onContinue?.(c)));
  }
  if (actions.onAddToDesk) {
    const desk = actBtn("xcard-btn", c.onDesk ? "On desk" : "Add to Desk", deskTip(c.title), (btn) =>
      actions.onAddToDesk?.(c, btn),
    );
    if (c.onDesk) {
      desk.classList.add("kept");
      desk.setAttribute("aria-label", `${c.title} is on the Source Desk`);
    }
    acts.append(desk);
  }
  if (actions.onBookmark) {
    // Bookmark is an ICON button (CSS pins it right). The glyph is a mask span; a
    // visually-hidden label keeps the accessible name + tests reading
    // "Bookmark"/"Bookmarked". The accent-filled saved state is driven by the
    // `kept` class (setBookmarkButton treats .xcard-bookmark as icon-only, so it
    // toggles the class without overwriting the icon).
    const mark = el("button", "xcard-btn xcard-bookmark");
    mark.type = "button";
    const aria = c.bookmarked ? `${c.title} is bookmarked` : bookmarkTip(c.title);
    mark.setAttribute("aria-label", aria);
    mark.title = aria;
    mark.setAttribute("aria-pressed", String(!!c.bookmarked));
    const glyph = el("span", "hi");
    glyph.setAttribute("aria-hidden", "true");
    mark.append(glyph, el("span", "visually-hidden", c.bookmarked ? "Bookmarked" : "Bookmark"));
    if (c.bookmarked) mark.classList.add("kept");
    mark.addEventListener("click", (e) => {
      e.stopPropagation();
      actions.onBookmark?.(c, mark);
    });
    acts.append(mark);
  }
  if (actions.onWhy) {
    const why = actBtn("xcard-btn xcard-btn-why", "Why?", `Why ${c.title} appears here`, () =>
      actions.onWhy?.(c),
    );
    why.setAttribute("aria-haspopup", "dialog");
    acts.append(why);
  }
  if (acts.children.length) card.append(acts);

  return card;
}

// A small, consistent relationship chip (e.g. "comments on this").
export function relChip(label: string, extraClass = ""): HTMLElement {
  const chip = el("span", `rel-chip${extraClass ? " " + extraClass : ""}`, label);
  return chip;
}

// The ONE teaching sentence per card verb. aria-label and the hover tooltip both
// come from here (via actBtn).
export function openTip(label: string): string {
  return `Open ${label} and continue your journey into it`;
}
export function deskTip(label: string): string {
  return `Keep ${label} for now - compare alongside the text`;
}
export function bookmarkTip(label: string): string {
  return `Bookmark ${label} for later - across sessions`;
}

// A labelled, propagation-stopped action button - shared by the xcard and the
// inline source-card action rows so the two stay one pattern.
export function actBtn(cls: string, label: string, aria: string, onClick: (btn: HTMLButtonElement) => void): HTMLButtonElement {
  const b = el("button", cls);
  b.type = "button";
  b.textContent = label;
  b.setAttribute("aria-label", aria);
  b.title = aria; // hover tooltip carries the same teaching sentence as the aria-label
  b.addEventListener("click", (e) => {
    e.stopPropagation();
    onClick(b);
  });
  return b;
}
